import { useState } from 'react';
import { MenuItem, Menu, Button } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import OpenAiUtil from '@/common/utils/OpenAiUtil';
import { checkModelUnsupport } from '@/component/system/handler/OrganizationProfileHandler';
import { PropTypes } from 'prop-types';
import useTranslationSubscription from '@/hooks/useTranslation';

ChatGPTModelDropdown.propTypes = {
    usingChatGptModel: PropTypes.any,
    setUsingChatGptModel: PropTypes.func,
    usingReasoningEffort: PropTypes.any,
    setUsingReasoningEffort: PropTypes.func,
    chatGPTModelListOptions: PropTypes.array,
    chatGPTModelListOfCorp: PropTypes.array
};

export default function ChatGPTModelDropdown({
    usingChatGptModel,
    setUsingChatGptModel,
    usingReasoningEffort,
    setUsingReasoningEffort,
    chatGPTModelListOptions,
    chatGPTModelListOfCorp
}) {
    useTranslationSubscription();

    const reasoningEfforts = OpenAiUtil.getReasoningEffortOptionsForModel(usingChatGptModel);
    const [anchorEl, setAnchorEl] = useState(null);
    const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
    const [hoveredModel, setHoveredModel] = useState(null);

    const handleOpenMenu = event => {
        setAnchorEl(event.currentTarget);
        setSubmenuAnchorEl(null);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setHoveredModel(null);
        setSubmenuAnchorEl(null);
    };

    const handleSelectHover = (event, model) => {
        setHoveredModel(model.id);
        if (OpenAiUtil.isSupportReasoning(model.id)) {
            setSubmenuAnchorEl(event.currentTarget);
            setUsingReasoningEffort(preState => {
                return OpenAiUtil.defaultReasoningEffort(model.id);
            });
            setUsingChatGptModel(model?.id);
        } else {
            setSubmenuAnchorEl(null);
        }
    };

    const handleSelectModel = modelId => {
        setUsingChatGptModel(modelId);
        handleCloseMenu();
        if (OpenAiUtil.isSupportReasoning(modelId)) {
            return setUsingReasoningEffort(OpenAiUtil.defaultReasoningEffort(modelId));
        }
        setUsingReasoningEffort(null);
    };

    const handleSelectSubmenu = (modelId, level) => {
        setUsingChatGptModel(modelId);
        setUsingReasoningEffort(level);
        handleCloseMenu();
    };

    const getGptModelAndReasoingEffort = () => {
        return usingReasoningEffort && OpenAiUtil.isSupportReasoning(usingChatGptModel)
            ? `${usingChatGptModel} ${usingReasoningEffort}`
            : usingChatGptModel;
    };

    const getModelItemClassName = modelId =>
        `font-style-gpt-model-menu ${
            checkModelUnsupport(modelId, chatGPTModelListOfCorp)
                ? 'd-hidden'
                : `model-ai-item${modelId === usingChatGptModel ? ' selected' : ''}`
        }`;

    return (
        <div className="chat-gpt-model-select">
            <Button
                className="font-style-gpt-model-menu"
                variant="outlined"
                onClick={handleOpenMenu}
                data-cy="header-top-select-gpt-model"
                style={{
                    textTransform: 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: "fit-content",
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
                endIcon={<ExpandMoreIcon />}
            >
                {getGptModelAndReasoingEffort()}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                getContentAnchorEl={null}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                PaperProps={{
                    style: {
                        maxHeight: chatGPTModelListOptions.length > 5 ? '12rem' : 'unset',
                        overflowY: chatGPTModelListOptions.length > 5 ? 'auto' : 'unset'
                    }
                }}
            >
                {chatGPTModelListOptions.map(item =>
                    OpenAiUtil.isSupportReasoning(item.id) ? (
                        <MenuItem
                            className={getModelItemClassName(item.id)}
                            key={item.id}
                            onClick={e => handleSelectHover(e, item)}
                            aria-haspopup="true"
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between'
                            }}
                            sx={{
                                '&:hover': {
                                    backgroundColor: '#F4F2F2'
                                }
                            }}
                        >
                            {item.name} <KeyboardArrowRight />
                        </MenuItem>
                    ) : (
                        <MenuItem
                            key={item.id}
                            onClick={() => handleSelectModel(item.name)}
                            className={getModelItemClassName(item.id)}
                            sx={{
                                '&:hover': {
                                    backgroundColor: '#F4F2F2'
                                }
                            }}
                        >
                            {item.name}
                        </MenuItem>
                    )
                )}
            </Menu>
            {/* Submenu */}
            <Menu
                anchorEl={submenuAnchorEl}
                open={OpenAiUtil.isSupportReasoning(hoveredModel) && Boolean(submenuAnchorEl)}
                onClose={() => setHoveredModel(null)}
                getContentAnchorEl={null}
                style={{ pointerEvents: 'none' }}
                MenuListProps={{
                    onMouseEnter: () => setHoveredModel(hoveredModel),
                    style: { pointerEvents: 'auto' }
                }}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
                PaperProps={{
                    style: {
                        marginLeft: '4px'
                    }
                }}
            >
                {reasoningEfforts.map(level => (
                    <MenuItem
                        key={level}
                        onClick={() => handleSelectSubmenu(usingChatGptModel, level)}
                        className={
                            `font-style-gpt-model-menu model-ai-item` +
                            (level === usingReasoningEffort ? ' selected' : '')
                        }
                        selected={level === usingReasoningEffort}
                        sx={{
                            '&:hover': {
                                backgroundColor: '#F4F2F2'
                            }
                        }}
                    >
                        {level}
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
}
